import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../utils/toast";
import { getRequest, postFormDataRequest, postRequest } from "../../utils/api";
import { useDropzone } from "react-dropzone";
import Loader from "../../Component/Loader/Loader";
import Swal from "sweetalert2";
import { logout } from "../../utils/auth";
import { toast } from "react-toastify";
import moment from "moment";
const KnowledgeBaseArticals = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Main Plan Data State
    const [formData, setFormData] = useState({
        cat_id: 0,
        cat_title: "",
        cat_sort_order: "",
        cat_description: "",
        cat_cover_image: null as File | null,
        cat_publish_type: 0,
    });
    const [coverPreview, setCoverPreview] = useState<string | null>(null);

    const onDrop = (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];

        if (!file) return;

        setFormData(prev => ({
            ...prev,
            cat_cover_image: file
        }));

        setCoverPreview(URL.createObjectURL(file));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": []
        },
        multiple: false,
        maxSize: 5 * 1024 * 1024
    });

    const handlePublish = async () => {
        if (!formData.cat_title) {
            showToast.error("Title is required.");
            return;
        }

        setLoading(true);

        try {
            const payload = new FormData();

            payload.append("cat_id", String(formData.cat_id));
            payload.append("cat_title", formData.cat_title);
            payload.append("cat_sort_order", formData.cat_sort_order);
            payload.append("cat_description", formData.cat_description);
            payload.append("cat_publish_type", String(formData.cat_publish_type));

            if (formData.cat_cover_image) {
                payload.append("cat_cover_image", formData.cat_cover_image);
            }
            const res = await postFormDataRequest("/knowledge/add-knowledge-category", payload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (res.status === "success") {
                showToast.success(res.message);
                navigate("/knowledge-base");
            } else {
                showToast.error(res.message);
            }
        } catch (error: any) {
            showToast.error(error.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const getKnowledgeCategoryDetails = async () => {
        setLoading(true);
        try {
            const payload = {
                _id: id
            }
            const res = await postRequest("/knowledge/get-knowledge-category-details", payload);
            if (res.status === "success") {
                setFormData({
                    cat_id: res.data.cat_id,
                    cat_title: res.data.cat_title,
                    cat_sort_order: res.data.cat_sort_order,
                    cat_description: res.data.cat_description,
                    cat_cover_image: null,
                    cat_publish_type: res.data.cat_publish_type,
                });
                if (res.data.cat_cover_image) {
                    setCoverPreview(res.image_base_path + res.data.cat_cover_image)
                }

            } else {
                showToast.error(res.message);
            }
        } catch (error: any) {
            showToast.error(error.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            getKnowledgeCategoryDetails();
            fetchKnowledgeCategoryArticalList()
        }
    }, [id]);

    const [knowledgeCategoryArtical, setKnowledgeCategoryArtical] = useState<any[]>([]);
    const [isLoader, setIsLoader] = useState<any>(true);
    const fetchKnowledgeCategoryArticalList = async () => {
        setLoading(true);
        try {
            const response = await getRequest(`/knowledge/knowledge-category-artical-list?artical_cat_id=${id}`);

            if (response.status === "success") {
                setKnowledgeCategoryArtical(response.data);
            } else if (response.status == 'session_expired') {
                setLoading(false)
                logout()
            }
            else {
                setLoading(false)
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
        } finally {
            setLoading(false);
        }
    };

    const deleteUser = async (item: any) => {
        const result = await Swal.fire({
            title: 'Are you sure want to permanent delete?',
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            icon: 'warning',
            customClass: {
                title: "custom-swal-title",
                popup: "custom-swal-popup",
                confirmButton: "custom-swal-button",
                cancelButton: "custom-swal-cancel"
            }
        });

        if (result.isConfirmed) {
            setIsLoader(true);
            try {
                const payload = {
                    _id: item?._id,
                };
                const response = await postRequest('/knowledge/deleteKnowledgeCategoryArtical', payload);
                if (response.status === 'success') {
                    fetchKnowledgeCategoryArticalList();
                    Swal.fire({
                        title: response.message,
                        icon: 'success',
                        customClass: {
                            popup: "custom-swal-popup"
                        }
                    });
                } else if (response.status === 'session_expired') {
                    setIsLoader(false);
                    logout()
                } else {
                    setIsLoader(false);
                    Swal.fire({
                        title: response.message,
                        icon: 'error',
                        customClass: {
                            popup: "custom-swal-popup"
                        }
                    });
                }
            } catch (error: any) {
                setIsLoader(false);
                toast.error(error.message);
            } finally {
                setIsLoader(false);
            }
        }
    };

    return (
        <>
            {loading && (<Loader />)}
            <div className="hk-pg-wrapper">
                <div className="hk-pg-body py-0">
                    <div className="page-wrap">
                        <div className="page-content ps-0">
                            <div className="page-detail-wrap">
                                <header className="page-header">
                                    <div>
                                        <div className="page-title">
                                            <h2 className="mb-0">New Category</h2>
                                        </div>
                                        <p className="text-muted mb-0">Manage your documentation — categories, articles, and visibility.</p>
                                    </div>
                                    <div className="page-options-wrap">
                                        <Link to={`/add-knowledge-category-artical/${id}`} className="btn btn-dark btn-rounded">
                                            <span>
                                                <span className="icon"><i className="ri-add-line bticon"></i></span>
                                                <span>New Artical</span>
                                            </span>
                                        </Link>
                                        <a className="btn btn-icon btn-flush-dark btn-rounded flush-soft-hover hk-navbar-togglable d-lg-inline-block d-none"
                                            href="#" data-bs-toggle="tooltip" data-bs-placement="top" title=""
                                            data-bs-original-title="Collapse">
                                            <span className="btn-icon-wrap">
                                                <span className="feather-icon"><i className="ri-arrow-up-s-line ri-lg"></i></span>
                                                <span className="feather-icon d-none"><i className="ri-arrow-down-s-line ri-lg"></i></span>
                                            </span>
                                        </a>
                                    </div>
                                </header>
                                <div className="page-body">
                                    <div data-simplebar className="nicescroll-bar">
                                        <div className="container-fluid">
                                            <div className="row g-3">
                                                <div className="col-lg-5">
                                                    <div className="card">
                                                        <div className="card-header">
                                                            <h6>Category Details</h6>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row g-3">
                                                                <div className="col-lg-9">
                                                                    <div className="form-group mb-0">
                                                                        <label>Title</label>
                                                                        <input type="text" name="cat_title" value={formData.cat_title} onChange={handleChange} className="form-control" />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-3">
                                                                    <div className="form-group mb-0">
                                                                        <label>Sort Order</label>
                                                                        <input type="text" name="cat_sort_order" value={formData.cat_sort_order} onChange={handleChange} className="form-control" />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label>Description</label>
                                                                        <textarea name="cat_description" value={formData.cat_description} onChange={handleChange} className="form-control"></textarea>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label>Description</label>
                                                                        <div
                                                                            {...getRootProps()}
                                                                            className="border border-dashed rounded p-4 text-center cursor-pointer"
                                                                        >
                                                                            <input {...getInputProps()} />

                                                                            {coverPreview ? (
                                                                                <img
                                                                                    src={coverPreview}
                                                                                    className="img-fluid rounded"
                                                                                    style={{ maxHeight: 180 }}
                                                                                />
                                                                            ) : (
                                                                                <div>
                                                                                    <p className="mb-1">
                                                                                        {isDragActive
                                                                                            ? "Drop image here..."
                                                                                            : "Drag & drop cover image"}
                                                                                    </p>
                                                                                    <small className="text-muted">
                                                                                        or click to browse
                                                                                    </small>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-12">
                                                                    <div className="d-flex justify-content-between align-items-center">

                                                                        <div className="d-flex align-items-start gap-3">
                                                                            {formData.cat_publish_type === 1 ?
                                                                                <i className="ri-eye-line fs-5 text-muted mt-1"></i>
                                                                                : <i className="ri-eye-off-line fs-5 text-muted mt-1"></i>
                                                                            }
                                                                            <div>
                                                                                <div className="fw-semibold">
                                                                                    {formData.cat_publish_type === 1 ? "Published" : "Draft"}
                                                                                </div>
                                                                                <div className="text-muted small">
                                                                                    {formData.cat_publish_type === 1
                                                                                        ? "Visible to public docs"
                                                                                        : "Hidden from public docs"}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div className="form-check form-switch">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                role="switch"
                                                                                checked={formData.cat_publish_type === 1}
                                                                                onChange={(e) =>
                                                                                    setFormData(prev => ({
                                                                                        ...prev,
                                                                                        cat_publish_type: e.target.checked ? 1 : 0
                                                                                    }))
                                                                                }
                                                                            />
                                                                        </div>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={handlePublish}
                                                                disabled={loading}
                                                            >
                                                                {loading ? 'Saving...' : 'Save'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-7">
                                                    <div className="card">
                                                        <div className="table-responsive">
                                                            <table className="table table-hover mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Title</th>
                                                                        <th className="text-center">Status</th>
                                                                        <th>Create Date</th>
                                                                        <th className="text-center">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {knowledgeCategoryArtical.length > 0 ? (
                                                                        knowledgeCategoryArtical.map((item) => (
                                                                            <tr key={item._id}>
                                                                                <td><span className="fw-bold">{item.artical_title}</span></td>
                                                                                <td className="text-center">
                                                                                    {item.artical_status === 1 ? (
                                                                                        <span className="badge bg-success">Active</span>
                                                                                    ) : (
                                                                                        <span className="badge bg-secondary">Inactive</span>
                                                                                    )}
                                                                                </td>
                                                                                <td>{moment(item.createdAt).format('DD MMM YYYY')}</td>
                                                                                <td className="text-center">
                                                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                                                        <div className="d-flex gap-1">
                                                                                            <Link
                                                                                                to={`/add-knowledge-category-artical/${id}/${item._id}`}
                                                                                                className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover"
                                                                                                data-bs-toggle="tooltip"
                                                                                                data-placement="top"
                                                                                                title="Edit"
                                                                                            >
                                                                                                <span className="icon">
                                                                                                    <i className="ri-pencil-fill"></i>
                                                                                                </span>
                                                                                            </Link>
                                                                                            <a
                                                                                                href="#"
                                                                                                onClick={(e) => {e.preventDefault();deleteUser(item)}}
                                                                                                className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover"
                                                                                                data-bs-toggle="tooltip"
                                                                                                data-placement="top"
                                                                                                title="Edit"
                                                                                            >
                                                                                                <span className="icon">
                                                                                                    <i className="ri-delete-bin-line"></i>
                                                                                                </span>
                                                                                            </a>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        !loading && (
                                                                            <tr>
                                                                                <td colSpan={8} className="text-center py-4">No artical found.</td>
                                                                            </tr>
                                                                        )
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>

    )
}
export default KnowledgeBaseArticals